import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaSearch} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import AgGridUtils from "../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import EncounterService from "../../../../services/web/encounter-service.js";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import ProjectUtils from "../../../../utils/project-utils.js";
import platePatternUtils from "../../../../utils/plate-pattern-utils.js";
import {buildEncounterListHeaders, buildEncounterListPayload, normalizeEncounterListResponse} from "./encounter-list-utils.js";
import PeriodInput from "../../../../components/input/period-input.jsx";
import {ENCOUNTER_STATUS_OPTIONS, encounterStatusLabel} from "../../../../utils/enums/encounter-status-enum.js";

const initialFilter = {
    dtStart: null,
    dtEnd: null,
    users: [],
    status: "",
    plate: "",
    cpf: "",
    textContained: "",
};

const initialSort = [{ field: "dtStart", dir: "desc" }];

export default function EncounterList(){
    const navigate = navigateHook();
    const gridRef = useRef(null);
    const [filterDraft, setFilterDraft] = useState(initialFilter);
    const [filterApplied, setFilterApplied] = useState(initialFilter);
    const [searchVersion, setSearchVersion] = useState(0);
    const [sort] = useState(initialSort);

    const { data: userIdentification } = useQuery({
       queryKey: ['user-identification'],
       queryFn: () => EncounterService.userIdentification().then((res) => (res ?? []).map(({ id, text }) => ({ value: id, label: text }))),
        initialData: [],
    });

    const buildPayload = useMemo(() => buildEncounterListPayload(filterApplied, sort), [filterApplied, sort]);
    const listHeaders = useMemo(() => buildEncounterListHeaders(buildPayload), [buildPayload]);

    const { data: encounterList = [], isFetching } = useQuery({
        queryKey: ["encounter-list", buildPayload, searchVersion],
        queryFn: async () => {
            const data = await EncounterService.list(listHeaders);
            return normalizeEncounterListResponse(data);
        },
        initialData: [],
    });

    const handleSearch = () => {
        setFilterApplied({...filterDraft});
        setSearchVersion(prev => prev + 1);
    };

    const handleClear = () => {
        setFilterDraft(initialFilter);
        setFilterApplied(initialFilter);
    };

    const columnDefs = useMemo(() => ([
        {headerName: "ID", field: "id", flex: 1},
        {headerName: "D/H", field: "dtStart", flex: 1, comparator: AgGridUtils.agGridCompareDateTime},
        {headerName: "Abordagem por", field: "userName", flex: 1,},
        {headerName: "Justificativa", field: "observation", flex: 1, comparator: AgGridUtils.nullLastComparator},
        {headerName: "Status", field: "status", flex: 1, valueFormatter: ({value}) => encounterStatusLabel(value)},
        {headerName: "Veículos", field: "vehicles_count", flex: 1, comparator: AgGridUtils.nullLastComparator},
        {headerName: "Indivíduos", field: "individuals_count", flex: 1, comparator: AgGridUtils.nullLastComparator},
        {headerName: "BO GCM", field: "bo_number", flex: 1, comparator: AgGridUtils.nullLastComparator},
    ]), []);

    const onCellClicked = ({ data, event }) => {
        if (!data?.id) return;

        const urlDev = `/encounter/form/${data.id}`;
        let isBlank = !!event.ctrlKey;

        navigate(urlDev, isBlank);
    };

    useGlobalKey("Enter", handleSearch);
    useGlobalKey("Escape", handleClear);

    return (
        <Template>
            <Template.Header>
                <Template.Title>Abordagem</Template.Title>
            </Template.Header>
            <Template.Body className="space-y-2 flex flex-col h-full min-h-0">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4">
                            <PeriodInput
                                startDateId={"dtStart"}
                                endDateId={"dtEnd"}
                                startDate={filterDraft.dtStart}
                                endDate={filterDraft.dtEnd}
                                setFilter={setFilterDraft}
                            />

                            <SelectComponent
                                id="users"
                                label="Abordagem por"
                                classNameWrapper="flex-2"
                                isMulti
                                options={userIdentification}
                                value={filterDraft.users}
                                onChange={(val) => setFilterDraft(prev => ({ ...prev, users: val ?? [] }))}
                            />

                            <SelectComponent
                                id="status"
                                label="Status"
                                classNameWrapper="flex-1"
                                type="select"
                                options={ENCOUNTER_STATUS_OPTIONS}
                                value={ENCOUNTER_STATUS_OPTIONS.find((option) => option.value === filterDraft.status) ?? null}
                                onChange={(val) => setFilterDraft((prev) => ({ ...prev, status: val?.value ?? "" }))}
                            />
                        </div>
                        <div className="flex gap-4 pt-3">
                            <InputForm
                                id="plate"
                                label="Placa"
                                classNameWrapper="flex-1"
                                mask=""
                                value={platePatternUtils.formatPlate(filterDraft.plate)}
                                onChange={(e) => setFilterDraft(prev => ({ ...prev, plate: e.target.value }))}
                            />

                            <InputForm
                                id="cpf"
                                label="CPF"
                                classNameWrapper="flex-1"
                                mask="000.000.000-00"
                                value={ProjectUtils.formatCpfToView(filterDraft.cpf)}
                                onChange={(e) => setFilterDraft(prev => ({ ...prev, cpf: e.target.value }))}
                            />

                            <InputForm
                                id="textContained"
                                label="Texto contido"
                                classNameWrapper="flex-2"
                                value={filterDraft.textContained}
                                onChange={(e) => setFilterDraft(prev => ({ ...prev, textContained: e.target.value }))}
                            />
                            <div className="flex gap-2">
                                <Button onClick={handleClear} target="primary-outline" title="Limpar">
                                    <FaEraser />
                                </Button>
                                <Button onClick={handleSearch} title="Buscar" disabled={isFetching}>
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card className="flex-1 min-h-0">
                    <Card.Body>
                        <div className="aggrid-row-pointer h-[calc(100vh-197px)]">
                            <AgGridReact
                                ref={gridRef}
                                rowData={encounterList}
                                columnDefs={columnDefs}
                                onCellClicked={onCellClicked}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus
                                gridOptions={{localeText: AgGridUtils.localization()}}
                                theme={AgGridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}